/* global window */
/* global document */

/**
 * Abtra Shop
 * Developer: Abdelhady "Hady" Salah
 * File: App.js
 * Description: Front-end application main component
 * Last Updated: 2023/06/23
 */
import React, { Suspense, lazy, startTransition, useCallback, useContext, useEffect, useRef, useState } from 'react';
import AppContext from './components/context/AppContext';
import { Route, Routes, useLocation } from 'react-router-dom';
import Navigation from './components/Navigation';
import { Row } from 'react-bootstrap';
import AnnouncementBar from './components/AnnouncementBar';
import { CartContext } from './components/context/CartContext';
import Product from './components/Product';
import { CustomerContext } from './components/context/CustomerContext';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingFallback from './components/LoadingFallback';
import Seo from './components/Seo';
import { Partytown } from '@builder.io/partytown/react';
import constants from './constants.json' assert { type: 'json' };
import useDynamicPath from './hooks/useDynamicPath';
import ExitIntentPopUp from './components/ExitIntentPopUp';
import useFireGtagEvent from './hooks/useFireGtagEvent';
import Homepage from './components/Homepage';
import FullHeightContainer from './components/FullHeightContainer';

import './styles/SearchPage.css';
import Category from './components/Category';
import SearchPage from './components/SearchPage';
import Footer from './components/Footer';
import SiteReviews from './components/SiteReviews';
import Wallet from './components/Wallet';
import ManageRecoveryEmail from './components/ManageRecoveryEmail';
import NewsletterBlock from './components/NewsletterBlock';
import { ToastContext } from './components/context/ToastContext';

// Lazy load components
const Article = lazy(() => import('./components/Article'));
const Page = lazy(() => import('./components/Page'));
const NewsletterConfirmationPage = lazy(() => import('./components/NewsletterConfirmationPage'));
const AccountRecoveryPage = lazy(() => import('./components/AccountRecoveryPage'));
const AccountRecoveryConfirmationPage = lazy(() => import('./components/AccountRecoveryConfirmationPage'));
const AccountRegistrationPage = lazy(() => import('./components/AccountRegistrationPage'));
const AccountVerificationConfirmationPage = lazy(() => import('./components/AccountVerificationConfirmationPage'));
const AccountVerificationPage = lazy(() => import('./components/AccountVerificationPage'));
const Collections = lazy(() => import('./components/Collections'));
const NotFound = lazy(() => import('./components/NotFound'));
const CollectionVendors = lazy(() => import('./components/CollectionVendors'));
const ProductIndex = lazy(() => import('./components/ProductIndex'));
const CategoryIndex = lazy(() => import('./components/CategoryIndex'));
const HelpPage = lazy(() => import('./components/HelpPage'));
const FaqPage = lazy(() => import('./components/FaqPage'));
const LoginPage = lazy(() => import('./components/LoginPage'));
const ProductsList = lazy(() => import('./components/ProductsList'));
const Blog = lazy(() => import('./components/Blog'));
const MyAccount = lazy(() => import('./components/MyAccount'));
const AccountOrders = lazy(() => import('./components/AccountOrders'));
const AccountAddresses = lazy(() => import('./components/AccountAddresses'));
const AccountOrder = lazy(() => import('./components/AccountOrder'));
const CartPage = lazy(() => import('./components/CartPage'));
const ChangePassword = lazy(() => import('./components/ChangePassword'));
const Questions = lazy(() => import('./components/Questions'));
const QuestionAnswers = lazy(() => import('./components/QuestionAnswers'));
const BrandList = lazy(() => import('./components/BrandList'));
const Brand = lazy(() => import('./components/Brand'));

const MOBILE_VIEW_BREAKPOINT = 800;

const App = () => {
    const { appContext } = useContext(AppContext);
    const { sendMessage } = useContext(ToastContext);
    const fireGA4Event = useFireGtagEvent();
    const appConfig = appContext.config.themeConfig;
    const {
        fetchCheckoutData,
    } = useContext(CartContext);
    const {
        customerData,
        fetchCustomerData,
    } = useContext(CustomerContext);
    const collectionPath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.COLLECTION_PATH });
    const productPath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.PRODUCT_PATH });
    const articlePath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.ARTICLE_PATH });
    const location = useLocation();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [userFetched, setUserFetched] = useState(false);
    const [userFirstName, setUserFirstName] = useState('');
    const [userLastName, setUserLastName] = useState('');
    const [navigationLinks, setNavigationLinks] = useState([]);
    const searchBoxRef = useRef(null);
    const [user, setUser] = useState(null);
    // Extract data from App Context
    const { userAgent } = appContext;
    // Get device type & handle scroll/resize
    const [width, setWidth] = useState(null);
    // Identify mobile/desktop
    const [isMobile, setIsMobile] = useState(!!userAgent?.isMobile);
    useEffect(() => {
        setIsMobile(typeof width === 'number'
            ? width <= MOBILE_VIEW_BREAKPOINT
            : !!userAgent?.isMobile);
    }, [width, userAgent.isMobile]);
    // Handle effects
    useEffect(() => {
        // Scroll to top on location changes
        document.documentElement.scrollTo(0, 0);
        // Fire the `page_view` event
        fireGA4Event('page_view', {
            page_location: new URL(location.pathname, process.env.APP_URL).href,
            language: navigator.language, // eslint-disable-line no-undef
            page_encoding: document.characterSet,
            user_agent: navigator.userAgent, // eslint-disable-line no-undef
            page_title: document.title, // eslint-disable-line no-undef
        });
    }, [location.pathname]);
    // Handle scroll & width changes
    useEffect(() => {
        // Handle window resize (width)
        const handleWindowSizeChange = () => {
            startTransition(() => {
                setWidth(window.outerWidth > 0 ? window.outerWidth : window.screen.width);
            });
        };
        handleWindowSizeChange();
        window.addEventListener('resize', handleWindowSizeChange);
        return () => {
            window.removeEventListener('resize', handleWindowSizeChange);
        };
    }, []);
    // Data fetching methods
    const fetchUserData = useCallback(async () => {
        try {
            const response = await fetch(new URL('api/user', process.env.APP_URL));
            const data = await response.json();
            if (data.user) {
                setUser(data.user);
                setUserFirstName(data.user.firstName ?? '');
                setUserLastName(data.user.lastName ?? '');
                setIsLoggedIn(true);
            } else {
                setIsLoggedIn(false);
            }
        } catch (e) {
            sendMessage(e.message ,'danger');
        } finally {
            setUserFetched(true);
        }
    }, []);
    const fetchNavigationData = useCallback(async () => {
        try {
            const url = new URL('api/nav', process.env.APP_URL);
            url.searchParams.append('collectionUrlPrefix', collectionPath[0].pathBeforeParams);
            url.searchParams.append('imagePlacement', appConfig.imagePlacements?.collection?.menuImage);
            url.searchParams.append('depth', 2);
            const response = await fetch(url);
            const data = await response.json();
            startTransition(() => setNavigationLinks([...data?.navData ?? [], ...appConfig.additionalHeaderLinks ?? []]));
        } catch (e) {
            sendMessage(e.message ,'danger');
        }
    }, []);
    useEffect(() => {
        fetchUserData();
        fetchNavigationData();
        fetchCheckoutData();
        fetchCustomerData();
    }, [fetchUserData, fetchNavigationData, fetchCheckoutData, fetchCustomerData]);
    // Render component
    return (<>
        <Seo
            seoTitle={appConfig.seoTitle ?? appContext.config.storeName}
            seoFaviconUrl={appContext.config.storeFaviconUrl}
            seoType={'website'}
            canonicalUrl={process.env.APP_URL}
        />
        <Partytown
            debug={process.env.NODE_ENV === 'development'}
            forward={['dataLayer.push']} />
        <header>
            <AnnouncementBar
                storefrontAnnouncement={appContext.config.storefrontAnnouncement} />
            <Navigation
                navData={navigationLinks}
                isMobile={isMobile}
                searchBoxRef={searchBoxRef} />
        </header>
        <ExitIntentPopUp
            bgImageUrl={appConfig.exitIntentPopUp.bgImageUrl}
            discountMessage={appConfig.exitIntentPopUp.discountMessage}
            discountCode={appConfig.exitIntentPopUp.discountCode}
            isLoggedIn={isLoggedIn}
            isMobile={isMobile}
        />
        <main
            style={{
                padding: '0',
                flexGrow: '1'
            }}
        >
            <ErrorBoundary>
                <FullHeightContainer>
                    <Suspense
                        fallback={<LoadingFallback />}>
                        <Row
                            style={{ margin: '0',
                                padding: '0' }}>
                            {/*<Sidebar />*/}
                            <div
                                className={'main-container__inner'}>
                                <Routes>
                                    <Route
                                        path={'/myaccount.asp'}
                                        element={<MyAccount
                                            userFirstName={userFirstName}
                                            userLastName={userLastName}
                                            userFetched={userFetched}
                                            isLoggedIn={isLoggedIn}
                                            user={user}
                                            isMobile={isMobile} />}></Route>
                                    <Route
                                        exact
                                        path={'/account/orders/:id'}
                                        element={<AccountOrder
                                            isMobile={isMobile} />}></Route>
                                    <Route
                                        path={'/orders.asp'}
                                        element={<AccountOrders />}
                                    />
                                    <Route
                                        path={'/address.asp'}
                                        element={(
                                            <AccountAddresses
                                                addressList={customerData?.MailingAddresses ?? []}
                                            />
                                        )}
                                    />
                                    <Route
                                        exact
                                        path={'/'}
                                        element={<Homepage
                                            isMobile={isMobile} />} />
                                    <Route
                                        exact
                                        path={'/default.asp'}
                                        element={<Homepage
                                            isMobile={isMobile} />} />
                                    <Route
                                        path={`${articlePath[0].pathBeforeParams}/tags/:tagValue`}
                                        element={<Blog
                                            blogDescription={appConfig.blogDescription}
                                            isMobile={isMobile} />} />
                                    <Route
                                        exact
                                        path={articlePath[0].fullPath}
                                        element={<Article
                                            isMobile={isMobile} />} />
                                    <Route
                                        exact
                                        path={articlePath[0].pathBeforeParams}
                                        element={<Blog
                                            blogDescription={appConfig.blogDescription}
                                            isMobile={isMobile} />} />
                                    <Route
                                        exact
                                        path={collectionPath[0].pathBeforeParams}
                                        element={<Collections
                                            isMobile={isMobile} />} />
                                    <Route
                                        exact
                                        path={collectionPath[0].fullPath}
                                        element={<Category
                                            searchBoxRef={searchBoxRef}
                                            isMobile={isMobile} />} />
                                    <Route
                                        exact
                                        path={collectionPath[0].pathBeforeParams + '/vendors'}
                                        element={<CollectionVendors
                                            isMobile={isMobile} />} />
                                    <Route
                                        exact
                                        path={'/searchresults.asp'}
                                        element={<Suspense
                                            fallback={null}
                                        >
                                            <SearchPage
                                                searchBoxRef={searchBoxRef}
                                                isMobile={isMobile} />
                                        </Suspense>} />
                                    <Route
                                        exact
                                        path={'/confirm-subscription'}
                                        element={<NewsletterConfirmationPage
                                            isMobile={isMobile} />} />
                                    <Route
                                        exact
                                        path={collectionPath[0].pathBeforeParams}
                                        element={<Collections
                                            isMobile={isMobile} />} />
                                    <Route
                                        exact
                                        path={productPath[0].fullPath}
                                        element={<Product
                                            isMobile={isMobile} />} />
                                    <Route
                                        exact
                                        path={'/pindex.asp'}
                                        element={<ProductIndex />} />
                                    <Route
                                        exact
                                        path={'/cindex.asp'}
                                        element={<CategoryIndex
                                            isMobile={isMobile} />} />
                                    <Route
                                        exact
                                        path={collectionPath[0].pathBeforeParams + '/:collectionHandle/' + productPath[0].pathBeforeParams + '/:handle'}
                                        element={<Product
                                            isMobile={isMobile} />} />
                                    <Route
                                        exact
                                        path={'/shoppingcart.asp'}
                                        element={<CartPage
                                            isMobile={isMobile} />} />
                                    <Route
                                        exact
                                        path={'/change-password'}
                                        element={<ChangePassword
                                            userFetched={userFetched}
                                            user={user} />} />
                                    <Route
                                        exact
                                        path={'/reset-password'}
                                        element={<AccountRecoveryPage
                                            isMobile={isMobile}
                                            isLoggedIn={isLoggedIn} />} />
                                    <Route
                                        exact
                                        path={'/recover-account'}
                                        element={<AccountRecoveryConfirmationPage
                                            isMobile={isMobile} />} />
                                    <Route
                                        exact
                                        path={'/register'}
                                        element={<AccountRegistrationPage
                                            isMobile={isMobile}
                                            isLoggedIn={isLoggedIn} />} />
                                    <Route
                                        exact
                                        path={'/start-verification'}
                                        element={<AccountVerificationPage
                                            isMobile={isMobile} />} />
                                    <Route
                                        exact
                                        path={'/verify-account'}
                                        element={<AccountVerificationConfirmationPage
                                            isMobile={isMobile} />} />
                                    <Route
                                        exact
                                        path={'/help.asp'}
                                        element={<HelpPage />} />
                                    <Route
                                        exact
                                        path={'/help_answer/:handle'}
                                        element={<FaqPage />} />
                                    <Route
                                        exact
                                        path={'/login.asp'}
                                        element={<LoginPage />} />
                                    <Route
                                        exact
                                        path={'/recovery-email'}
                                        element={<ManageRecoveryEmail
                                            userFetched={userFetched}
                                            reloadUserData={fetchUserData}
                                            user={user} />} />
                                    <Route
                                        exact
                                        path={'/productslist.asp'}
                                        element={<ProductsList
                                            isMobile={isMobile} />} />
                                    <Route
                                        exact
                                        path={'/:handle'}
                                        element={<Page
                                            isMobile={isMobile} />} />
                                    <Route
                                        exact
                                        path={'/:handle/:id.htm'}
                                        element={<Article
                                            isMobile={isMobile} />} />
                                    <Route
                                        exact
                                        path={'/wallet'}
                                        element={<Wallet
                                            user={user}
                                            isLoggedIn={isLoggedIn}
                                            userFetched={userFetched}/>} />
                                    <Route
                                        exact
                                        path={'/questions'}
                                        element={<Questions
                                            isMobile={isMobile} />} />
                                    <Route
                                        exact
                                        path={'/questions/Topic/:topicHandle/:topicId'}
                                        element={<Questions
                                            isMobile={isMobile} />} />
                                    <Route
                                        exact
                                        path={'/questions/:displayableId/:handle'}
                                        element={<QuestionAnswers
                                            isMobile={isMobile} />} />
                                    <Route
                                        path={'/brands'}
                                        element={<BrandList />} />
                                    <Route
                                        path={'/brands/:brand'}
                                        element={<Brand
                                            isMobile={isMobile} />} />
                                    <Route
                                        path={'*'}
                                        element={<NotFound />}
                                    />
                                </Routes>
                            </div>
                        </Row>
                    </Suspense>
                </FullHeightContainer>
                <SiteReviews
                    isMobile={isMobile}
                />
            </ErrorBoundary>
        </main>
        <Suspense
            fallback={<LoadingFallback />}
        >
            <NewsletterBlock
                newsletterText={'Subscribe to our newsletter'}/>
            <Footer />
        </Suspense>
    </>);
};

export default App;